import { gameState } from 'appworks/model/game-state';
import { Services } from 'appworks/services/services';
import { UIFlag, uiFlags } from 'appworks/ui/flags/ui-flags';
import { Contract } from 'appworks/utils/contracts/contract';
import { Sequence } from 'appworks/utils/contracts/sequence';
import { ValueList } from 'appworks/utils/value-list';
import { ClientController } from 'client-controller';
import { SlingoGameProgressResult } from 'slingo/model/results/slingo-game-progress-result';
import { slingoModel } from 'slingo/model/slingo-model';
import { SlotBetService } from 'slotworks/services/bet/slot-bet-service';
import { FowlInitState } from 'state-machine/states/fowl-init-state';

/**
 * Social init state, handles client bet stops
 *
 * @class SocialInitState
 * @typedef {SocialInitState}
 * @extends {PYLInitState}
 */
export class SocialInitState extends FowlInitState {
  private clientBetsHandled = false;
  private betsTimeout = 20000;

  /**
   * Send game ready, set bet stakes timeout
   *
   * @public
   * @override
   */
  public onEnter(): void {
    this.setupGameInactiveTimer();
    new Sequence([
      () => this.sendInitRequest(),
      () => Contract.wrap(() => uiFlags.set(UIFlag.AWAITING_RESPONSE, true)),
      () => Contract.wrap(() => ClientController.getInstance().sendGameReady(this)),
    ]).execute();
    setTimeout(() => {
      if (!this.clientBetsHandled) {
        this.enter();
      }
    }, this.betsTimeout);
  }

  /**
   * Enter game sequence
   *
   * @private
   */
  private enter(): void {
    new Sequence([
      () => Contract.wrap(() => uiFlags.set(UIFlag.AWAITING_RESPONSE, false)),
      () => this.showOnBoard(),
      () => this.gameLoaded(),
      () => this.startGame(),
    ]).then(() => this.complete());
  }

  /**
   * Inject bet stakes into model
   *
   * @private
   */
  private handleBets(): void {
    // Get the current game's stake if already in progress
    const progressData = gameState.getCurrentGame().getLatestResultOfType(SlingoGameProgressResult);
    const inProgressStake = progressData?.standardStake

    // Save the real config
    const gmrStakeConfig = slingoModel.read().stakeConfig;
    const gmrDefaultStake = gmrStakeConfig.amounts[gmrStakeConfig.defaultIndex];

    // Change the list of stakes to ours
    const model1 = slingoModel.read();
    model1.stakeConfig.amounts = ClientController.getInstance().betStakes.map(stake => stake * 100);
    slingoModel.write(model1);

    // Read what we just wrote because deepClone
    const stakeConfig = slingoModel.read().stakeConfig;

    // Find the next valid stake
    const amounts = stakeConfig.amounts;
    const defaultStake = amounts.find(stake => stake >= gmrDefaultStake) || amounts[0];
    const previousStake = inProgressStake || stakeConfig.lastStake || defaultStake;
    const newDefaultIndex = amounts.indexOf(defaultStake);

    // Convert defaultStake back to the new index
    const model2 = slingoModel.read();
    model2.stakeConfig.defaultIndex = newDefaultIndex;
    slingoModel.write(model2);

    // Save the new stake config
    Services.get(SlotBetService).setDefaults(
      new ValueList<number>(amounts, defaultStake, previousStake),
      new ValueList<number>([1], 1, 1),
    );

    // Backoffice stakes applied!
    this.clientBetsHandled = true;
  }

  /**
   * Inject bet stakes and enter game
   *
   * @public
   */
  public clientBetsReceived(): void {
    this.handleBets();
    this.enter();
  }
}
